// TenantSettingsDialog — edit a tenant's settings without a shell (tickets 111 / 205).
// PATCH /api/super/tenants/{id}; the host page (SuperAdminPage) already gates on
// is_super_admin. Two steps: edit, then review the before/after diff and confirm.
import React, { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { api } from '../api';
import { toast } from '../../hooks/use-toast';
import { errorMessage } from '../errors';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const PRODUCT_OPTIONS = [
  ['mortgage', 'Mortgage'],
  ['real_estate', 'Real Estate'],
  ['dual_licensed', 'Dual Licensed (Mortgage + Real Estate)'],
];
const KIND_OPTIONS = [['company', 'Company'], ['personal', 'Personal']];
const TEMPLATE_OPTIONS = [['modern', 'Modern'], ['classic', 'Classic'], ['bold', 'Bold']];
const TIER_OPTIONS = [['starter', 'Starter'], ['pro', 'Pro'], ['team', 'Team'], ['enterprise', 'Enterprise']];
const STATUS_OPTIONS = [
  ['active', 'Active'], ['trialing', 'Trialing'], ['past_due', 'Past due'], ['canceled', 'Canceled'], ['paused', 'Paused'],
];

const FIELDS = [
  { key: 'name', label: 'Name' },
  { key: 'slug', label: 'URL slug' },
  { key: 'product_type', label: 'Product', options: PRODUCT_OPTIONS },
  { key: 'kind', label: 'Kind', options: KIND_OPTIONS },
  { key: 'template', label: 'Site template', options: TEMPLATE_OPTIONS },
  { key: 'subscription_tier', label: 'Subscription tier', options: TIER_OPTIONS },
  { key: 'status', label: 'Billing status', options: STATUS_OPTIONS },
  { key: 'published', label: 'Public site published' },
];

const SIDES = { mortgage: ['Mortgage'], real_estate: ['Real Estate'], dual_licensed: ['Mortgage', 'Real Estate'] };

const slugify = (v) => v.toLowerCase().replace(/[^a-z0-9-]+/g, '-').replace(/-{2,}/g, '-');
// What the server stores: no leading or trailing hyphen (the input keeps them so typing "a-b" works).
const trimSlug = (v) => v.replace(/^-+|-+$/g, '');
const clean = (key, v) => {
  if (key === 'slug') return trimSlug(v);
  return typeof v === 'string' ? v.trim() : v;
};

const initialFrom = (t) => ({
  name: t.name || '',
  slug: t.slug || '',
  product_type: t.product_type || 'mortgage',
  kind: t.kind || 'company',
  template: t.template || 'modern',
  subscription_tier: t.subscription_tier || 'pro',
  status: t.status || 'active',
  published: !!t.published,
});

const labelOf = (field, value) => {
  if (field.key === 'published') return value ? 'Published' : 'Draft';
  const opt = field.options?.find(([v]) => v === value);
  return opt ? opt[1] : String(value);
};

// What the tenant's navigation gains or loses when the product changes.
export function productWarning(from, to) {
  const before = SIDES[from] || [];
  const after = SIDES[to] || [];
  const gains = after.filter((s) => !before.includes(s));
  const loses = before.filter((s) => !after.includes(s));
  const parts = [];
  if (gains.length) parts.push(`gains the ${gains.join(' and ')} pages`);
  if (loses.length) parts.push(`loses the ${loses.join(' and ')} pages (the data is kept)`);
  return parts.length ? `The tenant's navigation ${parts.join(' and ')}.` : null;
}

export default function TenantSettingsDialog({ tenant, onClose, onSaved }) {
  const original = useMemo(() => initialFrom(tenant), [tenant]);
  const [form, setForm] = useState(original);
  const [reviewing, setReviewing] = useState(false);
  const [saving, setSaving] = useState(false);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const changes = FIELDS.filter((f) => clean(f.key, form[f.key]) !== original[f.key]);
  const blank = !form.name.trim() || !trimSlug(form.slug);
  const warnings = [];
  if (changes.some((f) => f.key === 'product_type')) {
    const w = productWarning(original.product_type, form.product_type);
    if (w) warnings.push(w);
  }
  if (changes.some((f) => f.key === 'slug')) {
    warnings.push(`The public site moves from /site/${original.slug} to /site/${trimSlug(form.slug)}; the old address stops working.`);
  }
  if (changes.some((f) => f.key === 'status')) {
    warnings.push('Billing status changes here are not sent to Stripe.');
  }

  const save = async () => {
    const body = {};
    changes.forEach((f) => { body[f.key] = clean(f.key, form[f.key]); });
    setSaving(true);
    try {
      const { data } = await api.patch(`/super/tenants/${tenant.tenant_id}`, body);
      toast({
        title: 'Settings saved',
        description: `${tenant.name}: ${Object.keys(data.changed || {}).length} change(s) recorded in the audit log.`,
      });
      onSaved();
    } catch (e) {
      toast({ title: 'Save failed', description: errorMessage(e, 'Try again') });
      setReviewing(false);
    } finally {
      setSaving(false);
    }
  };

  const label = 'text-[11px] uppercase tracking-widest font-bold text-slate-500';

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-0 gap-0" data-testid="tenant-settings-dialog">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#6BA82E' }}>Owner Only</div>
          <DialogTitle className="font-serif text-xl font-bold" style={{ color: '#070B5F' }}>Settings — {tenant.name}</DialogTitle>
          <DialogDescription className="text-[12px] text-slate-500 mt-0.5">
            Every change is written to the audit log with its before and after values.
          </DialogDescription>
        </div>

        {!reviewing ? (
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className={label} htmlFor="ts-name">Name</label>
              <Input id="ts-name" type="text" value={form.name} maxLength={120} onChange={(e) => upd('name', e.target.value)} data-testid="ts-name" className="mt-1 h-10" />
            </div>
            <div>
              <label className={label} htmlFor="ts-slug">URL slug <span className="normal-case text-slate-400 font-normal">— /site/{form.slug || '…'}</span></label>
              <Input id="ts-slug" type="text" value={form.slug} maxLength={80} onChange={(e) => upd('slug', slugify(e.target.value))} data-testid="ts-slug" className="mt-1 h-10 font-mono" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              {FIELDS.filter((f) => f.options).map((f) => (
                <div key={f.key} className={f.key === 'product_type' ? 'col-span-2' : ''}>
                  <label className={label}>{f.label}</label>
                  <Select value={form[f.key]} onValueChange={(v) => upd(f.key, v)}>
                    <SelectTrigger data-testid={`ts-${f.key}`} className="mt-1 h-10 w-full bg-white text-[13px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {f.options.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2">
              <Checkbox id="ts-published" checked={form.published} onCheckedChange={(v) => upd('published', !!v)} data-testid="ts-published" />
              <label htmlFor="ts-published" className="text-sm text-slate-600">Public site is published</label>
            </div>
          </div>
        ) : (
          <div className="px-6 py-5 space-y-4" data-testid="ts-review">
            <div className="text-[12px] text-slate-600">Confirm these {changes.length} change(s):</div>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-[13px]">
              {changes.map((f) => (
                <li key={f.key} className="px-3 py-2 flex flex-wrap items-baseline gap-x-2" data-testid={`ts-diff-${f.key}`}>
                  <span className="font-semibold text-slate-700">{f.label}</span>
                  <span className="text-slate-500 line-through">{labelOf(f, original[f.key])}</span>
                  <span aria-hidden="true">→</span>
                  <span className="font-semibold" style={{ color: '#070B5F' }}>{labelOf(f, clean(f.key, form[f.key]))}</span>
                </li>
              ))}
            </ul>
            {warnings.map((w) => (
              <div key={w} className="text-[12px] rounded-lg px-3 py-2 bg-amber-50 text-amber-900 border border-amber-200" data-testid="ts-warning">{w}</div>
            ))}
          </div>
        )}

        <div className="px-6 py-4 border-t border-slate-100 flex items-center justify-end gap-2">
          {reviewing ? (
            <>
              <button onClick={() => setReviewing(false)} disabled={saving} className="text-[13px] px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="ts-back">Back</button>
              <button onClick={save} disabled={saving} data-testid="ts-confirm" className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white flex items-center gap-1.5 disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
                {saving && <Loader2 size={13} className="animate-spin" />} Save changes
              </button>
            </>
          ) : (
            <>
              <button onClick={onClose} className="text-[13px] px-4 py-2 rounded-lg text-slate-500 hover:bg-slate-100">Cancel</button>
              <button onClick={() => setReviewing(true)} disabled={!changes.length || blank} data-testid="ts-review-btn" className="text-[13px] font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50" style={{ background: 'linear-gradient(135deg, #070B5F 0%, #0f1580 100%)' }}>
                Review changes
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
